import React, { useEffect, useRef, useState, useCallback, Fragment } from "react";
import { Play, X, Volume2, VolumeX, Music2, BadgeCheck } from "lucide-react";
import LazyVideo from "./LazyVideo";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";
const T = "/thumbnails";

const categories = [
  {
    id: "automotive",
    title: "Automotive",
    handle: "@freysmedia · Premium Cars",
    sub: "Hochleistung. Lack. Asphalt. Cinema-Cuts mit Premium-Marken auf realen Straßen statt Studio.",
    accent: "hsl(20, 95%, 60%)",
    highlights: [
      { label: "Porsche",   emoji: "🏁" },
      { label: "Lambo",     emoji: "🐂" },
      { label: "Rolls",     emoji: "👑" },
      { label: "Audi",      emoji: "💎" },
      { label: "Mercedes",  emoji: "⭐" },
      { label: "Dubai",     emoji: "🌆" },
    ],
    reels: [
      { id: 1,  title: "Porsche GT3 RS · Berge",      video: `${R2}/videos/tag-3-gt3-rs-berge-2.mp4`,           poster: `${T}/reel-1.webp` },
      { id: 17, title: "Porsche 911 · Hegauablick",   video: `${R2}/videos/porsche-carrera-hegauablick.mp4`,    poster: `${T}/reel-17.webp` },
      { id: 16, title: "Porsche GT3 · Donautal",      video: `${R2}/videos/porsche-gt3-donautal-friedingen.mp4`, poster: `${T}/reel-16.webp` },
      { id: 4,  title: "Lamborghini Urus SE · Hegau", video: `${R2}/videos/lamborghini-urus-se.mp4`,            poster: `${T}/reel-4.webp` },
      { id: 2,  title: "Lamborghini Urus · Dubai",    video: `${R2}/videos/urus-blau-dubai.mp4`,                poster: `${T}/reel-2.webp` },
      { id: 3,  title: "Rolls-Royce Cullinan · Dubai", video: `${R2}/videos/cullinan-dubai-skyline.mp4`,         poster: `${T}/reel-3.webp` },
      { id: 14, title: "Audi S8 · Deißlingen",        video: `${R2}/videos/audi-s8-deisslingen.mp4`,            poster: `${T}/reel-14.webp` },
      { id: 15, title: "Mercedes G-Klasse · Donautal", video: `${R2}/videos/g-klasse-donautal-friedingen.mp4`,   poster: `${T}/reel-15.webp` },
    ],
  },
  {
    id: "stadt",
    title: "Stadt & Event",
    handle: "@freysmedia · City Marketing",
    sub: "Brauchtum, Stadt-Marketing — Reels für die Wirtschaftsförderung und Eventkampagnen.",
    accent: "hsl(280, 75%, 60%)",
    highlights: [
      { label: "VS Stadt",   emoji: "🏛️" },
      { label: "Markt",      emoji: "🎄" },
      { label: "Abendflair", emoji: "🌙" },
      { label: "Wirtschaft", emoji: "💼" },
    ],
    reels: [
      { id: 8,  title: "VS · Weihnachtsmärkte 2024",  video: `${R2}/videos/vs-weihnachtmaerkte-rueckblick-2024.mp4`, poster: `${T}/reel-8.webp` },
      { id: 9,  title: "VS · Abendflair",             video: `${R2}/videos/stadt-villingen-abendflair.mp4`,     poster: `${T}/reel-9.webp` },
    ],
  },
  {
    id: "lifestyle",
    title: "Lifestyle & Brands",
    handle: "@freysmedia · Editorial",
    sub: "Produkt-Drehs, Editorial-Sets, Musikvideos — Cinema-Look für Marken die im Feed stoppen wollen.",
    accent: "hsl(330, 80%, 65%)",
    highlights: [
      { label: "Seedos",     emoji: "🌱" },
      { label: "Editorial",  emoji: "📸" },
      { label: "Models",     emoji: "👗" },
      { label: "Music",      emoji: "🎵" },
      { label: "Studio",     emoji: "💡" },
    ],
    reels: [
      { id: 5,  title: "Seedos · Der Snack",          video: `${R2}/videos/seedos-der-snack-der-dich-findet.mp4`, poster: `${T}/reel-5.webp` },
      { id: 6,  title: "Seedos · Salted",             video: `${R2}/videos/seedos-salted.mp4`,                  poster: `${T}/reel-6.webp` },
      { id: 7,  title: "Seedos · Einbruch",           video: `${R2}/videos/seedos-einbruch-reel.mp4`,           poster: `${T}/reel-7.webp` },
      { id: 10, title: "Models · Editorial",          video: `${R2}/videos/alle-models-10-12.mp4`,              poster: `${T}/reel-10.webp` },
      { id: 13, title: "Musikvideo · Trailer",        video: `${R2}/videos/musikvideo-trailer.mp4`,             poster: `${T}/reel-13.webp` },
    ],
  },
  {
    id: "recruiting",
    title: "Recruiting",
    handle: "@freysmedia · Employer Branding",
    sub: "Echte Mitarbeiter-Storys statt Hochglanz. Reels die Bewerber abholen, weil sie ehrlich sind.",
    accent: "hsl(210, 90%, 60%)",
    highlights: [
      { label: "KOD",        emoji: "🛡️" },
      { label: "Service",    emoji: "🤝" },
      { label: "Fiehn",      emoji: "🏗️" },
    ],
    reels: [
      { id: 21, title: "KOD · Kommunales Ordnungsamt", video: `${R2}/videos/kod-recruiting-vs.mp4`,             poster: `${T}/kod-recruiting-vs.webp` },
      { id: 11, title: "Servicemitarbeiter (m/w/d)",  video: `${R2}/videos/servicemitarbeiter-mwd.mp4`,         poster: `${T}/reel-11.webp` },
      { id: 12, title: "Fiehn Gebäudeautomation",     video: `${R2}/videos/fiehn-recruting-reel.mp4`,           poster: `${T}/reel-12.webp` },
    ],
  },
  {
    id: "nightlife",
    title: "Nightlife & Fitness",
    handle: "@freysmedia · High Energy",
    sub: "Hohe ISO, satte Farben, schnelle Cuts — Reels für Locations, an denen die Energie zählt.",
    accent: "hsl(160, 70%, 55%)",
    highlights: [
      { label: "Club",       emoji: "🎧" },
      { label: "Gym",        emoji: "💪" },
      { label: "Night",      emoji: "🌃" },
    ],
    reels: [
      { id: 18, title: "Club Menschmeier · Lahr",     video: `${R2}/videos/club-menschmeier.mp4`,               poster: `${T}/reel-18.webp` },
      { id: 19, title: "Izumi E-Gym · Villingen",     video: `${R2}/videos/izumi-egym-villingen.mp4`,           poster: `${T}/reel-19.webp` },
    ],
  },
];

const stats = [
  { number: "200+", label: "Social Media Videos" },
  { number: "5",    label: "Jahre Erfahrung" },
  { number: "70+",  label: "Kunden" },
];

// (Likes/Comments-Fake-Stats entfernt — wir zeigen keine erfundenen Zahlen.)

// Smart column count → never a single lonely tile in last row
// Returns Tailwind class for desktop columns
const colsForDesktop = (n) => {
  switch (n) {
    case 1: return "lg:grid-cols-1";
    case 2: return "lg:grid-cols-2";
    case 3: return "lg:grid-cols-3";
    case 4: return "lg:grid-cols-4";
    case 5: return "lg:grid-cols-5";
    case 6: return "lg:grid-cols-3";   // 2×3
    case 7: return "lg:grid-cols-4";   // 4+3
    case 8: return "lg:grid-cols-4";   // 2×4
    case 9: return "lg:grid-cols-3";   // 3×3
    case 10: return "lg:grid-cols-5";  // 2×5
    default: return "lg:grid-cols-4";
  }
};

// Reel-Card: Poster als Standard, Video läuft NUR bei Hover (Performance!)
const ReelCard = ({ reel, accent, onOpen }) => {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);

  // IntersectionObserver nur noch fürs Lazy-Mounten des <video>-Elements
  // + Sicherheits-Pause, falls man mit laufendem Video rausscrollt.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setInView(true);
          else videoRef.current?.pause();
        });
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const startPlayback = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };
  const stopPlayback = () => videoRef.current?.pause();

  return (
    <div
      ref={wrapperRef}
      className="group relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black border border-white/10 hover:border-[hsl(var(--accent))]/60 transition-all cursor-pointer hover:scale-[1.02]"
      onClick={() => onOpen(reel)}
      onMouseEnter={startPlayback}
      onMouseLeave={stopPlayback}
      data-testid={`reel-${reel.id}`}
    >
      {/* Video */}
      {inView && (
        <video
          ref={videoRef}
          src={reel.video + "#t=0.5"}
          poster={reel.poster}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      {!inView && (
        <img
          src={reel.poster}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Subtle dim for legibility */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent via-50% to-black/75" />

      {/* Bottom caption + music — schlank, ohne Insta-Fake-Layer */}
      <div className="absolute inset-x-0 bottom-0 z-20 p-2.5 pb-3">
        <div className="text-[11px] sm:text-xs text-white/95 font-medium leading-snug line-clamp-2 mb-1 drop-shadow-md">
          {reel.title}
        </div>
        <div className="flex items-center gap-1 text-[9px] text-white/70">
          <Music2 className="w-3 h-3" />
          <span className="truncate">Original-Audio · freysmedia</span>
        </div>
      </div>

      {/* Center play button hint on hover */}
      <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
        </div>
      </div>
    </div>
  );
};

// Section: alternating Editorial layout
const ShowreelSection = ({ cat, index, reverse }) => {
  const num = String(index + 1).padStart(2, "0");
  const cols = colsForDesktop(cat.reels.length);
  return (
    <section
      id={`reel-${cat.id}`}
      className="relative py-10 sm:py-14 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start"
      data-testid={`reel-cat-${cat.id}`}
    >
      <div
        aria-hidden="true"
        className={`pointer-events-none select-none absolute top-0 z-0 hidden lg:block font-black leading-none ${
          reverse ? "right-0 -mr-4" : "left-0 -ml-4"
        }`}
        style={{
          fontSize: "16rem",
          WebkitTextStroke: `1.5px ${cat.accent}40`,
          color: "transparent",
          letterSpacing: "-0.05em",
        }}
      >
        {num}
      </div>

      {/* Headline (sticky desktop) — schlank, ohne Insta-Profile-Pretending */}
      <div className={`relative z-10 lg:col-span-4 ${reverse ? "lg:order-2" : "lg:order-1"}`}>
        <div className="lg:sticky lg:top-24 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-xs tracking-[0.3em] font-semibold" style={{ color: cat.accent }}>
              {num} —
            </span>
            <span className="h-px w-10" style={{ backgroundColor: `${cat.accent}66` }} />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05]">{cat.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-sm">
            {cat.sub}
          </p>
          <div
            className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest font-semibold border"
            style={{ backgroundColor: `${cat.accent}15`, color: cat.accent, borderColor: `${cat.accent}55` }}
          >
            {cat.reels.length} Reels
          </div>
        </div>
      </div>

      {/* Tiles */}
      <div className={`relative z-10 lg:col-span-8 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
        <div className={`grid grid-cols-2 ${cols} gap-2.5 sm:gap-3`}>
          {cat.reels.map((reel) => (
            <ReelCard key={reel.id} reel={reel} accent={cat.accent} onOpen={cat.onOpen} />
          ))}
        </div>
      </div>
    </section>
  );
};

const SectionDivider = ({ nextNum, accent }) => (
  <div className="relative py-6 flex items-center gap-4 sm:gap-6" aria-hidden="true">
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
    <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase font-mono text-muted-foreground">
      <span>↓ Cut to</span>
      <span style={{ color: accent }} className="font-bold text-base font-sans tracking-tight">
        {String(nextNum).padStart(2, "0")}
      </span>
    </div>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
  </div>
);

// Modal player
const ReelModal = ({ reel, onClose }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = orig;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    const p = v.play();
    if (p && p.catch) p.catch(() => { v.muted = true; setMuted(true); v.play().catch(() => {}); });
  }, [reel]);

  if (!reel) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
        <X className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); const v = videoRef.current; if (v) { v.muted = !v.muted; setMuted(v.muted); } }}
        className="absolute top-4 right-16 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white"
      >
        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
      </button>
      <div className="relative max-h-[90vh] w-auto" style={{ aspectRatio: "9 / 16" }} onClick={(e) => e.stopPropagation()}>
        <video ref={videoRef} src={reel.video} poster={reel.poster} controls autoPlay loop playsInline className="h-[90vh] w-auto rounded-2xl bg-black object-contain" />
        <div className="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 text-sm text-white pointer-events-none">
          <div className="font-semibold">@freysmedia</div>
          <div className="text-white/85">{reel.title}</div>
        </div>
      </div>
    </div>
  );
};

const Showreel = () => {
  const [active, setActive] = useState(null);
  const open = useCallback((reel) => setActive(reel), []);
  const close = useCallback(() => setActive(null), []);
  const totalReels = categories.reduce((s, c) => s + c.reels.length, 0);
  const cats = categories.map((c) => ({ ...c, onOpen: open }));

  return (
    <section id="showreel" className="py-20 bg-secondary/30 overflow-hidden" data-testid="showreel-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="showreel-title">
            Reels & Social Media
          </h2>
          <p className="text-base sm:text-xl text-muted-foreground">
            {totalReels} Reels · {cats.length} Kategorien · scrollen wie ein Insta-Feed.
            Klick auf ein Reel öffnet den Vollbild-Player mit Sound.
          </p>
        </div>

        {/* Editorial Sections */}
        <div className="max-w-7xl mx-auto">
          {cats.map((cat, i) => (
            <Fragment key={cat.id}>
              <ShowreelSection cat={cat} index={i} reverse={i % 2 === 1} />
              {i < cats.length - 1 && <SectionDivider nextNum={i + 2} accent={cats[i + 1].accent} />}
            </Fragment>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16" data-testid="stats-section">
          {stats.map((stat, index) => (
            <div key={index} className="bg-secondary/50 backdrop-blur-sm border border-white/5 rounded-2xl p-8 text-center hover-tilt" data-testid={`stat-${index}`}>
              <div className="text-4xl sm:text-5xl font-bold mb-2 neon-text text-[hsl(var(--accent))]">{stat.number}</div>
              <div className="text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {active && <ReelModal reel={active} onClose={close} />}
    </section>
  );
};

export default Showreel;
